"use strict";
/*
 * Copyright (C) 2021 JET PROTOCOL HOLDINGS, LLC.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.JetClient = void 0;
const anchor_1 = require("@project-serum/anchor");
const _1 = require(".");
const layout_1 = require("./layout");
const util_1 = require("./util");
const hooks_1 = require("../common/hooks");
/**
 * TODO:
 * @export
 * @class JetClient
 */
class JetClient {
    /**
     * Creates an instance of JetClient.
     * @param {Program<Jet>} program
     * @param {boolean} [devnet]
     * @memberof JetClient
     */
    constructor(program, devnet) {
        this.program = program;
        this.devnet = devnet;
    }
    /**
     * Create a new client for interacting with the Jet lending program.
     * @param {Provider} provider The provider with wallet/network access that can be used to send transactions.
     * @param {boolean} [devnet] Flag to determine if the connection is for devnet
     * @returns {Promise<JetClient>} The client
     * @memberof JetClient
     */
    static async connect(provider, devnet) {
        const idl = await anchor_1.Program.fetchIdl(_1.JET_ID, provider);
        return new JetClient(new anchor_1.Program(idl, _1.JET_ID, provider), devnet);
    }
    /**
     * Return all `Obligation` program accounts that have been created
     * @param {GetProgramAccountsFilter[]} [filters]
     * @returns {Promise<ProgramAccount<Obligation>[]>}
     * @memberof JetClient
     */
    async allObligations(filters) {
        return this.program.account.obligation.all(filters);
    }
    /**
     * Decodes a buffer of account data into a usable
     * `Obligation` object.
     * @param {Buffer} b
     * @returns {ObligationAccount}
     * @memberof JetClient
     */
    decodeObligation(b) {
        const o = this.program.coder.accounts.decode(JetClient.OBLIGATION_ACCOUNT_NAME, b);
        o.loans = layout_1.PositionInfoStructList.decode(Buffer.from(o.loans)).map(util_1.parsePosition);
        o.collateral = layout_1.PositionInfoStructList.decode(Buffer.from(o.collateral)).map(util_1.parsePosition);
        return o;
    }
    /**
     * Encodes the argued `Obligation` object into a `Buffer`.
     * @param {ObligationAccount} o
     * @returns {Promise<Buffer>}
     * @memberof JetClient
     */
    encodeObligation(o) {
        return this.program.coder.accounts.encode(JetClient.OBLIGATION_ACCOUNT_NAME, o);
    }
    /**
     * @static
     * @param {Provider} provider
     * @returns {(JetClient | undefined)} JetClient | undefined
     * @memberof JetClient
     */
    static use(provider, devnet) {
        return hooks_1.Hooks.usePromise(async () => provider && JetClient.connect(provider, devnet), [provider]);
    }
}
exports.JetClient = JetClient;
JetClient.OBLIGATION_ACCOUNT_NAME = "Obligation";
//# sourceMappingURL=client.js.map